import React from 'react';
import Image, { StaticImageData } from 'next/image';
import clsx from 'clsx';
import { BookOpenIcon, FilmIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import Link from 'next/link';
import Button from '@/components/Button';
import { CustomerDataType } from '../customerData';

type categoryItems = {
  [key: string]: React.ReactElement;
};

const categoryIconDict: categoryItems = {
  'Customer story': <BookOpenIcon className="size-4" />,
  Blog: <BookOpenIcon className="size-4" />,
  Video: <FilmIcon className="size-4" />,
};

const gradientColorDict = {
  red: 'to-gradient-red',
  beige: 'to-gradient-beige',
  purple: 'to-gradient-purple',
  orange: 'to-gradient-orange',
  blue: 'to-gradient-blue',
  yellow: 'to-gradient-yellow',
  grey: 'to-gradient-grey',
  darkblue: 'to-gradient-darkblue',
};

export interface BGImageProps extends Pick<CustomerDataType, 'icon' | 'type' | 'text' | 'target'> {
  bgImage: StaticImageData;
  gradientColor?: 'red' | 'beige' | 'purple' | 'orange' | 'blue' | 'yellow' | 'grey' | 'darkblue';
}

const imageCardBorderRadius = 'rounded-2xl xl:rounded-3xl lg:rounded-3xl md:rounded-3xl';

const BGImageCard = ({ bgImage, icon: Logo, type, text, target, gradientColor }: BGImageProps) => {
  const innerCardContent = (
    <>
      {Logo && (
        <Logo
          monochrome
          className="absolute left-6 top-6 z-20 w-48 text-white md:left-8 md:top-8 lg:left-8 lg:top-8 xl:left-8 xl:top-8"
        />
      )}

      {gradientColor && (
        <div
          className={clsx(
            `absolute z-10 -mb-6 -ml-6 h-full w-full bg-gradient-to-b from-transparent md:-mb-8 md:-ml-8 lg:-mb-8 lg:-ml-8 xl:-mb-8 xl:-ml-8`,
            gradientColorDict[gradientColor],
            imageCardBorderRadius,
          )}
        ></div>
      )}
      <Image
        alt=""
        src={bgImage}
        placeholder="blur"
        quality={75}
        fill
        className={clsx('bg-cover object-cover', imageCardBorderRadius)}
      />

      {type && (
        <div className="z-10 mb-2 flex items-center gap-2 justify-self-end">
          {categoryIconDict[type as keyof categoryItems]}
          <p className="text-body">{type}</p>
        </div>
      )}
      <div className="z-10 flex w-full items-center justify-between gap-6">
        <p className="text-eyebrow-strong max-w-[26rem]">{text}</p>

        {target && (
          <Button
            outline
            className="self-end from-primary to-secondary transition-all duration-300 ease-in-out group-hover:-translate-y-1 group-hover:bg-gradient-to-b"
            size="sm"
            roundedFull
            variant="ghost-light"
            onClick={() => {}}
          >
            <ArrowRightIcon className="size-5 md:size-6" />
          </Button>
        )}
      </div>
    </>
  );

  if (target) {
    return target.includes('http') ? (
      <a
        href={target}
        target="_blank"
        rel="noopener"
        className={clsx(
          'group relative flex h-[22rem] w-full flex-col items-start justify-end bg-gray-2 p-6 text-white md:h-[26rem] md:p-8 lg:h-[26rem] lg:p-8 xl:h-[26rem] xl:p-8',
          imageCardBorderRadius,
        )}
        draggable={false}
      >
        {innerCardContent}
      </a>
    ) : (
      <Link
        href={target}
        className={clsx(
          'group relative flex h-[22rem] w-full flex-col items-start justify-end bg-gray-2 p-6 text-white md:h-[26rem] md:p-8 lg:h-[26rem] lg:p-8 xl:h-[26rem] xl:p-8',
          imageCardBorderRadius,
        )}
        draggable={false}
      >
        {innerCardContent}
      </Link>
    );
  }

  return (
    <div
      className={clsx(
        'relative flex h-[22rem] w-full flex-col items-start justify-end bg-gray-2 p-6 text-white md:p-8 lg:p-8 xl:p-8',
        imageCardBorderRadius,
      )}
    >
      {innerCardContent}
    </div>
  );
};

export default BGImageCard;
